import { Alert } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import {
  DailyLabellerStatsEntry,
  LabellerDetails,
} from "src/store/api/generatedApi";
import {
  DateRangeFilterBar,
  useDateRangeFilters,
} from "components/manageLabellers/dateRangeFilterBar";
import {
  DailyActivityView,
  DailyStatsForTenant,
  useDailyActivityViewState,
} from "components/manageLabellers/dailyActivityView";
import {
  TenantDailyStatsLoader,
  TenantLabellersLoader,
} from "./TenantDailyActivityLoaders";
import { mergeTenantState, TenantName } from "./TenantLabellerStatsLoader";

type TenantLabellersState = Readonly<{
  labellers: readonly LabellerDetails[];
  isLoading: boolean;
  isError: boolean;
}>;

export type CrossTenantLabeller = LabellerDetails & {
  tenantNames: readonly string[];
};

type TenantDailyStatsState = Readonly<{
  labellerId: string;
  dailyStats: readonly DailyLabellerStatsEntry[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}>;

// Held whole rather than by id: a date range the labeller has no activity in
// drops them from the roster, and re-deriving from it would clear the choice.
// The retained copy is still narrowed to the selected tenants, so it cannot
// keep querying one the user has deselected.
export const resolveSelectedLabeller = (
  chosenLabeller: CrossTenantLabeller | null | undefined,
  mergedLabellers: readonly CrossTenantLabeller[],
  labellersLoading: boolean,
  selectedTenantNames: readonly string[]
): CrossTenantLabeller | null => {
  if (chosenLabeller === undefined) {
    return labellersLoading ? null : (mergedLabellers[0] ?? null);
  }
  if (chosenLabeller === null) {
    return null;
  }
  const inRoster = mergedLabellers.find(
    (labeller) => labeller.id === chosenLabeller.id
  );
  if (inRoster !== undefined) {
    return inRoster;
  }
  const stillSelectedTenantNames = chosenLabeller.tenantNames.filter(
    (tenantName) => selectedTenantNames.includes(tenantName)
  );
  return stillSelectedTenantNames.length > 0
    ? { ...chosenLabeller, tenantNames: stillSelectedTenantNames }
    : null;
};

type CrossTenantDailyActivityTabProps = Readonly<{
  tenantNames: readonly string[];
}>;

const NO_TENANT_NAMES: readonly string[] = [];

const statsForLabeller = (
  stateByTenant: Record<TenantName, TenantDailyStatsState>,
  tenantName: string,
  labellerId: string | undefined
): TenantDailyStatsState | undefined => {
  const state = stateByTenant[tenantName];
  return state !== undefined && state.labellerId === labellerId
    ? state
    : undefined;
};

const withTenant = (
  tenantNames: readonly string[],
  tenantName: string
): readonly string[] =>
  tenantNames.includes(tenantName) ? tenantNames : [...tenantNames, tenantName];

export const CrossTenantDailyActivityTab = ({
  tenantNames,
}: CrossTenantDailyActivityTabProps) => {
  const { t } = useTenantTranslation();
  const dateFilters = useDateRangeFilters();
  const viewState = useDailyActivityViewState();

  const [labellersByTenant, setLabellersByTenant] = useState<
    Record<TenantName, TenantLabellersState>
  >({});

  const handleLabellersLoaded = useCallback(
    (
      tenantName: string,
      labellers: readonly LabellerDetails[],
      isLoading: boolean,
      isError: boolean
    ) => {
      setLabellersByTenant((previous) =>
        mergeTenantState(previous, tenantName, {
          labellers,
          isLoading,
          isError,
        })
      );
    },
    []
  );

  const labellersLoading = tenantNames.some(
    (name) => labellersByTenant[name]?.isLoading ?? true
  );

  const mergedLabellers: CrossTenantLabeller[] = useMemo(() => {
    const byId = new Map<string, CrossTenantLabeller>();
    for (const tenantName of tenantNames) {
      const state = labellersByTenant[tenantName];
      if (!state) continue;
      for (const labeller of state.labellers) {
        if (labeller.labeller_type !== "human") continue;
        const existing = byId.get(labeller.id);
        byId.set(
          labeller.id,
          existing
            ? {
                ...existing,
                tenantNames: withTenant(existing.tenantNames, tenantName),
              }
            : { ...labeller, tenantNames: [tenantName] }
        );
      }
    }
    return [...byId.values()];
  }, [labellersByTenant, tenantNames]);

  // `undefined` means the user has not chosen yet, so the first labeller is
  // auto-selected; `null` means they explicitly cleared the selection.
  const [chosenLabeller, setChosenLabeller] = useState<
    CrossTenantLabeller | null | undefined
  >(undefined);

  const selectedLabeller = useMemo(
    () =>
      resolveSelectedLabeller(
        chosenLabeller,
        mergedLabellers,
        labellersLoading,
        tenantNames
      ),
    [labellersLoading, mergedLabellers, chosenLabeller, tenantNames]
  );

  const handleSelectLabeller = useCallback(
    (labeller: CrossTenantLabeller | null) => {
      setChosenLabeller(labeller);
    },
    []
  );

  const activeTenantNames = selectedLabeller?.tenantNames ?? NO_TENANT_NAMES;
  const labellingTenantNames = viewState.showLabelling
    ? activeTenantNames
    : NO_TENANT_NAMES;
  const reviewingTenantNames = viewState.showReviewing
    ? activeTenantNames
    : NO_TENANT_NAMES;

  const [labellingByTenant, setLabellingByTenant] = useState<
    Record<TenantName, TenantDailyStatsState>
  >({});
  const [reviewingByTenant, setReviewingByTenant] = useState<
    Record<TenantName, TenantDailyStatsState>
  >({});

  const handleLabellingLoaded = useCallback(
    (
      tenantName: string,
      labellerId: string,
      dailyStats: readonly DailyLabellerStatsEntry[],
      isLoading: boolean,
      isFetching: boolean,
      isError: boolean
    ) => {
      setLabellingByTenant((previous) =>
        mergeTenantState(previous, tenantName, {
          labellerId,
          dailyStats,
          isLoading,
          isFetching,
          isError,
        })
      );
    },
    []
  );

  const handleReviewingLoaded = useCallback(
    (
      tenantName: string,
      labellerId: string,
      dailyStats: readonly DailyLabellerStatsEntry[],
      isLoading: boolean,
      isFetching: boolean,
      isError: boolean
    ) => {
      setReviewingByTenant((previous) =>
        mergeTenantState(previous, tenantName, {
          labellerId,
          dailyStats,
          isLoading,
          isFetching,
          isError,
        })
      );
    },
    []
  );

  const selectedLabellerId = selectedLabeller?.id;

  const isLoading =
    labellingTenantNames.some(
      (name) =>
        statsForLabeller(labellingByTenant, name, selectedLabellerId)
          ?.isLoading ?? true
    ) ||
    reviewingTenantNames.some(
      (name) =>
        statsForLabeller(reviewingByTenant, name, selectedLabellerId)
          ?.isLoading ?? true
    );
  const isFetching =
    labellingTenantNames.some(
      (name) =>
        statsForLabeller(labellingByTenant, name, selectedLabellerId)
          ?.isFetching ?? true
    ) ||
    reviewingTenantNames.some(
      (name) =>
        statsForLabeller(reviewingByTenant, name, selectedLabellerId)
          ?.isFetching ?? true
    );

  const failedTenantNames = useMemo(() => {
    const failed = tenantNames.filter(
      (name) => labellersByTenant[name]?.isError ?? false
    );
    const failedStats = [
      ...labellingTenantNames.filter(
        (name) =>
          statsForLabeller(labellingByTenant, name, selectedLabellerId)
            ?.isError ?? false
      ),
      ...reviewingTenantNames.filter(
        (name) =>
          statsForLabeller(reviewingByTenant, name, selectedLabellerId)
            ?.isError ?? false
      ),
    ];
    return [...new Set([...failed, ...failedStats])];
  }, [
    tenantNames,
    labellersByTenant,
    labellingTenantNames,
    reviewingTenantNames,
    labellingByTenant,
    reviewingByTenant,
    selectedLabellerId,
  ]);

  const labellingPerTenant = useMemo<DailyStatsForTenant[]>(
    () =>
      activeTenantNames.map((name) => ({
        tenantName: name,
        dailyStats:
          statsForLabeller(labellingByTenant, name, selectedLabellerId)
            ?.dailyStats ?? [],
      })),
    [activeTenantNames, labellingByTenant, selectedLabellerId]
  );

  const reviewingPerTenant = useMemo<DailyStatsForTenant[]>(
    () =>
      activeTenantNames.map((name) => ({
        tenantName: name,
        dailyStats:
          statsForLabeller(reviewingByTenant, name, selectedLabellerId)
            ?.dailyStats ?? [],
      })),
    [activeTenantNames, reviewingByTenant, selectedLabellerId]
  );

  return (
    <>
      {tenantNames.map((tenantName) => (
        <TenantLabellersLoader
          key={tenantName}
          tenantName={tenantName}
          start={dateFilters.start?.toISOString() ?? undefined}
          end={dateFilters.end?.toISOString() ?? undefined}
          filterMode={dateFilters.filterMode}
          onLoaded={handleLabellersLoaded}
        />
      ))}
      {selectedLabeller &&
        labellingTenantNames.map((tenantName) => (
          <TenantDailyStatsLoader
            key={`labelling-${tenantName}-${selectedLabeller.id}`}
            tenantName={tenantName}
            labellerId={selectedLabeller.id}
            start={dateFilters.start?.toISOString() ?? undefined}
            end={dateFilters.end?.toISOString() ?? undefined}
            filterMode={dateFilters.filterMode}
            sessionTypes={["labelling"]}
            onLoaded={handleLabellingLoaded}
          />
        ))}
      {selectedLabeller &&
        reviewingTenantNames.map((tenantName) => (
          <TenantDailyStatsLoader
            key={`reviewing-${tenantName}-${selectedLabeller.id}`}
            tenantName={tenantName}
            labellerId={selectedLabeller.id}
            start={dateFilters.start?.toISOString() ?? undefined}
            end={dateFilters.end?.toISOString() ?? undefined}
            filterMode={dateFilters.filterMode}
            sessionTypes={["reviewing"]}
            onLoaded={handleReviewingLoaded}
          />
        ))}
      {failedTenantNames.length > 0 ? (
        <Alert severity="warning">
          {t("partialTenantResults", { tenants: failedTenantNames.join(", ") })}
        </Alert>
      ) : null}
      <DailyActivityView
        labellers={mergedLabellers}
        labellersLoading={labellersLoading}
        selectedLabeller={selectedLabeller}
        onSelectLabeller={handleSelectLabeller}
        labellingByTenant={labellingPerTenant}
        reviewingByTenant={reviewingPerTenant}
        isLoading={isLoading}
        isFetching={isFetching}
        state={viewState}
        toolbarLeading={<DateRangeFilterBar filters={dateFilters} />}
      />
    </>
  );
};
